import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import MergeIcon from '@mui/icons-material/Merge';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

interface CartItem {
  Id: number;
  ProductId: string;
  ProductName: string;
  Quantity: number;
  UnitPrice: number;
  TotalPrice: number;
}

interface MergeTablesConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  sourceTableName: string;
  targetTableName: string;
  sourceItems: CartItem[];
  targetItems: CartItem[];
  isProcessing: boolean;
}

const MergeTablesConfirmModal: React.FC<MergeTablesConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  sourceTableName,
  targetTableName,
  sourceItems,
  targetItems,
  isProcessing,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Sepet toplamlarını hesapla
  const calculateTotal = (items: CartItem[]) => {
    return items.reduce((sum, item) => sum + item.TotalPrice, 0);
  };

  const sourceTotal = calculateTotal(sourceItems);
  const targetTotal = calculateTotal(targetItems);
  const mergedTotal = sourceTotal + targetTotal;

  // Fiyat formatlama
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
    }).format(price);
  };

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle sx={{ px: { xs: 2, md: 3 }, bgcolor: 'warning.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MergeIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
          <Typography variant="h6" component="span" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
            Masa Birleştirme Onayı
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
        {/* Bilgi Mesajı */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
            <strong>{sourceTableName}</strong> masasındaki ürünler <strong>{targetTableName}</strong> masasına eklenecek.
            Bu işlem geri alınamaz!
          </Typography>
        </Alert>

        {/* 2 Sütunlu Sepet Gösterimi */}
        <Grid container spacing={2}>
          {/* Sol: Kaynak Masa Sepeti */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: 'error.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TableRestaurantIcon color="error" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                  {sourceTableName} (Aktarılacak)
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              {sourceItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                  Sepet boş
                </Typography>
              ) : (
                <List dense sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {sourceItems.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="medium">
                            {item.ProductName}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {item.Quantity} x {formatPrice(item.UnitPrice)} = {formatPrice(item.TotalPrice)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  Toplam:
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="error.main">
                  {formatPrice(sourceTotal)}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Sağ: Hedef Masa Sepeti */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: 'success.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TableRestaurantIcon color="success" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                  {targetTableName} (Hedef)
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              {targetItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                  Sepet boş
                </Typography>
              ) : (
                <List dense sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {targetItems.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="medium">
                            {item.ProductName}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {item.Quantity} x {formatPrice(item.UnitPrice)} = {formatPrice(item.TotalPrice)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  Toplam:
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {formatPrice(targetTotal)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Birleştirilmiş Toplam */}
        <Paper 
          elevation={3} 
          sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon />
            <Typography variant="h6" fontWeight="bold">
              Birleştirilmiş Toplam:
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold">
            {formatPrice(mergedTotal)}
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, pb: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button
          onClick={onClose}
          disabled={isProcessing}
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
        >
          İptal
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isProcessing}
          variant="contained"
          color="warning"
          size={isMobile ? 'small' : 'medium'}
          startIcon={isProcessing ? <CircularProgress size={20} /> : <MergeIcon />}
        >
          {isProcessing ? 'Birleştiriliyor...' : 'Sepeti Birleştir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeTablesConfirmModal;

